/**
 * PLAT-11914 — soft line breaks inside a caption.
 *
 * A soft break keeps a caption one caption (one clip, one time window) but
 * starts a new line inside it. It lives on the clip as data, never as a `\n`
 * token in the transcript or the translation — both are read by other views.
 * The renderer receives it as a `\n` at the end of the animation unit that
 * ends the line (`KaraokeClip.toRenderJSON`).
 *
 * Two anchors, one per caption path:
 *  - original language: the id of the token the line ends after
 *    (`KaraokeClip.softBreakAfterTokenIds`). A token id survives a hard
 *    split or merge untouched, so those only filter or concat the list.
 *  - translated: a position inside one of the clip's `translatedPhrases`
 *    (`KaraokeClip.softBreakPhrasePositions`) — the next line starts at
 *    `offset` in phrase `phraseIndex`. Hard splits and merges re-number
 *    phrases, so they rebase these with the helpers below.
 *
 * Pure: no MobX, no React, no editor.
 */

import { isCjkOpeningPunctuation } from '@src/utility/strings';

import type { TranscriptToken } from '../types';
import type { AnimationSourceUnit } from './translatedKaraokeAnimation';

/** A translated-caption soft break: the next line starts at `offset` of phrase `phraseIndex`. */
export interface PhraseSoftBreak {
  phraseIndex: number;
  offset: number;
}

const isWhitespaceToken = (token: TranscriptToken): boolean =>
  token.type === 'punct' && (token.value ?? '').trim() === '';

const isOpeningPunctToken = (token: TranscriptToken): boolean =>
  token.type === 'punct' && isCjkOpeningPunctuation(token.value ?? '');

/**
 * The token a soft break after `tokenId` really ends the line on, or `null`
 * when no line can start after it (it is the row's last visible token, or not
 * in the row).
 *
 * The break is carried past the punctuation that follows the word, so `、`,
 * `。` or `」` never start the next line — the line-start rule of Japanese
 * typesetting. An opening bracket (`「`) starts the next line instead of
 * ending this one: the walk stops before one, and a break on one moves back
 * to the token before it (the cursor stops on words, so Shift+Enter before
 * `「日本」` anchors on the bracket).
 */
export function resolveSoftBreakToken(
  tokens: readonly TranscriptToken[],
  tokenId: string,
): string | null {
  let index = tokens.findIndex(token => token.id === tokenId);
  if (index < 0) return null;
  while (index >= 0 && isOpeningPunctToken(tokens[index])) index -= 1;
  if (index < 0) return null; // the bracket opens the row: no line above it
  while (
    index + 1 < tokens.length &&
    tokens[index + 1].type === 'punct' &&
    !isWhitespaceToken(tokens[index + 1]) &&
    !isOpeningPunctToken(tokens[index + 1])
  ) {
    index += 1;
  }
  const startsLine = tokens
    .slice(index + 1)
    .some(token => token.type === 'text');
  return startsLine ? tokens[index].id : null;
}

/**
 * Add a break on `tokenId` (already resolved), or remove the one already
 * there. A stored break matches when it resolves to the same token in
 * `tokens`: one stored on a word before punctuation was added after it ends
 * the line at that punctuation now, so toggling from there removes it.
 */
export function toggleTokenSoftBreak(
  breaks: readonly string[],
  tokenId: string,
  tokens: readonly TranscriptToken[] = [],
): string[] {
  const endsAt = (id: string) => resolveSoftBreakToken(tokens, id) ?? id;
  const kept = breaks.filter(id => endsAt(id) !== tokenId);
  return kept.length < breaks.length ? kept : [...breaks, tokenId];
}

/**
 * The tokens a row's soft breaks end a line after, as the captions panel
 * draws them: each stored break resolved past any punctuation that follows
 * it (`resolveSoftBreakToken`) — the same line end the renderer uses. A break
 * that no longer ends a line is left out.
 */
export function softBreakLineEndIds(
  tokens: readonly TranscriptToken[],
  breaks: readonly string[],
): Set<string> {
  const ids = new Set<string>();
  for (const id of breaks) {
    const lineEnd = resolveSoftBreakToken(tokens, id);
    if (lineEnd) ids.add(lineEnd);
  }
  return ids;
}

/** Whether a soft break sits at `position` (same phrase, same offset). */
export function hasPhraseSoftBreak(
  breaks: readonly PhraseSoftBreak[],
  position: PhraseSoftBreak,
): boolean {
  return breaks.some(
    b => b.phraseIndex === position.phraseIndex && b.offset === position.offset,
  );
}

/**
 * Add a soft break at `position`, or remove the one already there. A break
 * at the very start of the caption is refused (returns `null`): there is no
 * line above it to end.
 */
export function togglePhraseSoftBreak(
  breaks: readonly PhraseSoftBreak[],
  position: PhraseSoftBreak,
): PhraseSoftBreak[] | null {
  if (hasPhraseSoftBreak(breaks, position)) {
    return breaks.filter(
      b =>
        !(
          b.phraseIndex === position.phraseIndex && b.offset === position.offset
        ),
    );
  }
  if (position.phraseIndex === 0 && position.offset <= 0) return null;
  return [...breaks, { ...position }].sort(
    (a, b) => a.phraseIndex - b.phraseIndex || a.offset - b.offset,
  );
}

/**
 * Rebase soft breaks across a hard split of a translated clip. The right
 * clip starts at `offset` of phrase `phraseIndex`; `rightTrim` is how many
 * leading characters the split trimmed from the right half of that phrase.
 * A soft break exactly at the split point is dropped — the hard break
 * replaces it.
 */
export function splitPhraseSoftBreaks(
  breaks: readonly PhraseSoftBreak[],
  split: PhraseSoftBreak,
  rightTrim = 0,
): { left: PhraseSoftBreak[]; right: PhraseSoftBreak[] } {
  const left: PhraseSoftBreak[] = [];
  const right: PhraseSoftBreak[] = [];
  for (const b of breaks) {
    if (
      b.phraseIndex < split.phraseIndex ||
      (b.phraseIndex === split.phraseIndex && b.offset < split.offset)
    ) {
      left.push({ ...b });
    } else if (b.phraseIndex > split.phraseIndex) {
      right.push({
        phraseIndex: b.phraseIndex - split.phraseIndex,
        offset: b.offset,
      });
    } else if (b.offset > split.offset) {
      const offset = b.offset - split.offset - rightTrim;
      if (offset > 0) right.push({ phraseIndex: 0, offset });
    }
  }
  return { left, right };
}

/**
 * Rebase soft breaks across a merge of two translated clips. When
 * `fusedPhrase` is set, the left clip's last phrase and the right clip's
 * first phrase became one phrase; `rightPhraseShift` is where the right
 * phrase's text starts inside the fused text.
 */
export function mergePhraseSoftBreaks(
  left: readonly PhraseSoftBreak[],
  right: readonly PhraseSoftBreak[],
  leftPhraseCount: number,
  fusedPhrase: { rightPhraseShift: number } | null,
): PhraseSoftBreak[] {
  const shifted = right.map(b => {
    if (!fusedPhrase) {
      return { phraseIndex: b.phraseIndex + leftPhraseCount, offset: b.offset };
    }
    return b.phraseIndex === 0
      ? {
          phraseIndex: leftPhraseCount - 1,
          offset: b.offset + fusedPhrase.rightPhraseShift,
        }
      : { phraseIndex: b.phraseIndex + leftPhraseCount - 1, offset: b.offset };
  });
  return [...left.map(b => ({ ...b })), ...shifted];
}

/**
 * The token soft breaks that still end a line inside a rebuilt row of
 * `tokens`: the token is in the row and a word follows it. A break whose
 * token left the row, or that now ends the row (a hard break already ends
 * that line), is dropped. `undefined` when none survive.
 */
export function keepTokenSoftBreaks(
  breaks: readonly string[] | undefined,
  tokens: readonly TranscriptToken[],
): string[] | undefined {
  const kept = (breaks ?? []).filter(
    id => resolveSoftBreakToken(tokens, id) != null,
  );
  return kept.length > 0 ? kept : undefined;
}

/**
 * Rebase phrase soft breaks onto the phrases that survive a filter (reconcile
 * drops whole entries whose source words are gone). `kept` is a subset of
 * `phrases` — same objects, same order. A break in a dropped phrase goes with
 * it, and one that would now open the caption is dropped: there is no line
 * above it to end. `undefined` when none survive.
 */
export function keepPhraseSoftBreaks<T>(
  breaks: readonly PhraseSoftBreak[] | undefined,
  phrases: readonly T[],
  kept: readonly T[],
): PhraseSoftBreak[] | undefined {
  const keptSet = new Set(kept);
  const newIndex = new Map<number, number>();
  phrases.forEach((phrase, index) => {
    if (keptSet.has(phrase)) newIndex.set(index, newIndex.size);
  });
  const rebased = (breaks ?? []).flatMap(b => {
    const phraseIndex = newIndex.get(b.phraseIndex);
    if (phraseIndex == null || (phraseIndex === 0 && b.offset <= 0)) return [];
    return [{ phraseIndex, offset: b.offset }];
  });
  return rebased.length > 0 ? rebased : undefined;
}

/**
 * Re-deal the soft breaks of a phrase's single-phrase rows when those rows are
 * rebuilt from their joined text (`rebuildPhraseClips`, on moving or removing
 * a synthetic break). `oldRows` and `newRowTexts` are in order and are found
 * in `joinedText` by search; each break follows its character into whichever
 * new row now holds it. A break that lands on a new row's start is dropped (a
 * hard break ends that line), as are the breaks of a row that can't be found.
 */
export function redealPhraseSoftBreaks(
  joinedText: string,
  oldRows: readonly {
    text: string;
    softBreaks?: readonly PhraseSoftBreak[];
  }[],
  newRowTexts: readonly string[],
): (PhraseSoftBreak[] | undefined)[] {
  // Where each text's untrimmed start sits in `joinedText`, or null.
  const locate = (texts: readonly string[]): (number | null)[] => {
    let cursor = 0;
    return texts.map(text => {
      const trimmed = text.trim();
      const at = joinedText.indexOf(trimmed, cursor);
      if (at < 0) return null;
      cursor = at + trimmed.length;
      return at - (text.length - text.trimStart().length);
    });
  };

  const oldStarts = locate(oldRows.map(row => row.text));
  const positions = oldRows.flatMap((row, i) => {
    const start = oldStarts[i];
    if (start == null) return [];
    return (row.softBreaks ?? [])
      .filter(b => b.phraseIndex === 0)
      .map(b => start + b.offset);
  });

  const newStarts = locate(newRowTexts);
  return newRowTexts.map((text, i) => {
    const start = newStarts[i];
    if (start == null) return undefined;
    const offsets = positions
      .filter(position => position > start && position < start + text.length)
      .map(position => ({ phraseIndex: 0, offset: position - start }));
    return offsets.length > 0 ? offsets : undefined;
  });
}

/**
 * End the line at `units[index]`: drop its trailing whitespace and append
 * `\n`. A trailing opening bracket (`「`) moves to the start of the next
 * unit so it opens the next line rather than dangling at the end of this
 * one. Mutates `units`.
 */
export function endLineAtUnit(
  units: AnimationSourceUnit[],
  index: number,
): void {
  const unit = units[index];
  const next = units[index + 1];
  if (!unit || !next) return;
  let value = unit.value.trimEnd();
  let carried = '';
  while (value.length > 0 && isCjkOpeningPunctuation(value.slice(-1))) {
    carried = value.slice(-1) + carried;
    value = value.slice(0, -1).trimEnd();
  }
  if (value.length === 0) return;
  unit.value = `${value}\n`;
  if (carried) next.value = carried + next.value;
}

/**
 * Split animation units into lines at the units that end in `\n`. Each line
 * keeps its `\n` on its last unit.
 */
export function splitUnitsIntoLines(
  units: readonly AnimationSourceUnit[],
): AnimationSourceUnit[][] {
  const lines: AnimationSourceUnit[][] = [[]];
  for (const unit of units) {
    lines[lines.length - 1].push(unit);
    if (unit.value.endsWith('\n')) lines.push([]);
  }
  return lines.filter(line => line.length > 0);
}

/**
 * Where a soft break is toggled: after a token (original language, the clip
 * is found by the token) or at a position inside a translated clip.
 */
export type CaptionSoftBreakAnchor =
  | { tokenId: string }
  | { clipId: string; phraseIndex: number; offset: number };
